import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { usePluginDetail } from "@/api/queries/plugins";
import { useUpdatePluginInBrain } from "@/api/mutations/plugins";

// #294 (UC-4): nach der Freigabe einer Plugin-ÄNDERUNG anbieten, die betroffenen Agents
// sofort in-place zu aktualisieren (statt auf deren nächsten Start/User-Klick zu warten).
// Reiner Bedien-Ablauf — der Server koppelt nichts; idempotent (CC skippt Gleichstand).
// Muster: FreischaltenNachFreigabeDialog (#288 UC-6).
export function UpdateAgentsNachFreigabeDialog({
  open,
  onOpenChange,
  marketplaceId,
  pluginName,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  marketplaceId: string;
  pluginName: string;
}) {
  const detail = usePluginDetail(marketplaceId, pluginName);
  const update = useUpdatePluginInBrain();
  const [running, setRunning] = useState(false);
  const [ergebnis, setErgebnis] = useState<Array<{ agentId: string; ok: boolean; version?: string | null }> | null>(null);

  const betroffen = (detail.data?.usedBy ?? []).filter((u) => u.enabled);

  async function ausrollen() {
    setRunning(true);
    const res = await Promise.allSettled(
      betroffen.map((u) => update.mutateAsync({ agentId: u.agentId, marketplaceId, pluginName })),
    );
    const rows = betroffen.map((u, i) => {
      const r = res[i]!;
      return { agentId: u.agentId, ok: r.status === "fulfilled", version: r.status === "fulfilled" ? r.value.installedVersion : null };
    });
    setErgebnis(rows);
    setRunning(false);
    const failed = rows.filter((r) => !r.ok).length;
    if (failed === 0) toast.success(`${rows.length === 1 ? "1 Agent" : `${rows.length} Agents`} aktualisiert.`);
    else toast.error(`${failed} von ${rows.length} Updates fehlgeschlagen — Details im Dialog.`);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="update-agents-dialog">
        <DialogHeader>
          <DialogTitle>Agents jetzt aktualisieren?</DialogTitle>
          <DialogDescription>
            {betroffen.length === 0
              ? `Kein Agent nutzt „${pluginName}" aktiv — es gibt nichts auszurollen.`
              : `${betroffen.length === 1 ? "1 Agent nutzt" : `${betroffen.length} Agents nutzen`} „${pluginName}" — das Update läuft in-place im laufenden Brain (kein Neustart). Ohne Ausrollen kommt es beim nächsten Start (Auto-Update) bzw. per Update-Knopf.`}
          </DialogDescription>
        </DialogHeader>

        {betroffen.length > 0 && (
          <ul className="max-h-56 space-y-1 overflow-y-auto" data-testid="update-agents-list">
            {betroffen.map((u) => {
              const r = ergebnis?.find((x) => x.agentId === u.agentId);
              return (
                <li key={u.agentId} className="flex items-center gap-2 rounded-md border border-border px-2 py-1.5 text-[12.5px]">
                  <span className="font-medium">{u.agentName ?? u.agentId}</span>
                  <span className="ml-auto">
                    {r === undefined ? (
                      <Badge variant="outline">{u.scope}</Badge>
                    ) : r.ok ? (
                      <Badge variant="enrolled">aktualisiert{r.version ? ` (${r.version})` : ""}</Badge>
                    ) : (
                      <Badge variant="destructive">fehlgeschlagen</Badge>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={running}>
            {ergebnis ? "Schließen" : "Später"}
          </Button>
          {betroffen.length > 0 && !ergebnis && (
            <Button onClick={() => void ausrollen()} disabled={running || detail.isLoading} data-testid="update-agents-confirm">
              {running ? "Aktualisiere …" : "Jetzt aktualisieren"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
